/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react'
import styled from '@emotion/styled'
import { ChangeEvent, Fragment, FunctionComponent, KeyboardEvent, MouseEvent, useCallback, useEffect, useState } from 'react'
import Typist from 'react-typist'
import { COLOR_PALETTE } from '../GlobalStyle'
import { Nullable } from '../types'
import { getKeyboardKey } from '../utils/keyboard'
import { generateSubTestId } from '../utils/testid'
import { Icon, IconButton } from './Icon'
import { TextHighlight } from './TextHighlight'

interface SearchInputProps {
    initialText?: string
    onSubmit: (text: string) => void
    onToggleFilters: () => void
    filtersOpened: boolean
    isOnResultsPage?: boolean
    onClearClick?: () => void
    autocompleteOptions?: string[]
    placeholder?: string
    withTypist?: boolean
    disabled?: boolean
    showFilterOption?: boolean
    dataTestId?: string
    styles?: SerializedStyles
}

const SearchInput: FunctionComponent<React.PropsWithChildren<SearchInputProps>> = ({
    initialText = '',
    onSubmit,
    onToggleFilters,
    filtersOpened,
    isOnResultsPage,
    onClearClick,
    autocompleteOptions,
    placeholder = '',
    withTypist,
    disabled,
    showFilterOption = true,
    dataTestId,
    styles = {},
    ...other
}) => {
    const [searchText, setSearchText] = useState(initialText)
    const [showTypist, setShowTypist] = useState(withTypist)
    const [isFocused, setIsFocused] = useState(false)
    const [filteredOptions, setFilteredOptions] = useState<string[]>([])
    const [showAutocompleteOptions, setShowAutocompleteOptions] = useState(false)
    const [selectedAutocompleteOption, setSelectedAutocompleteOption] = useState<Nullable<number>>(null)

    const filterFunction = (input: string, option: string): boolean => {
        if (input && input.length > 0) {
            return option.toLowerCase().indexOf(input.toLowerCase()) > -1
        }

        return true
    }

    const updateShowAutocompleteOptions = useCallback(
        (options: string[]) => {
            if (options.length > 0) {
                if (options.length === 1) {
                    if (options[0].toLowerCase() === searchText.toLowerCase()) {
                        setShowAutocompleteOptions(false)
                    } else {
                        setShowAutocompleteOptions(true)
                    }
                } else {
                    setShowAutocompleteOptions(true)
                }
            } else {
                setShowAutocompleteOptions(false)
            }
        },
        [searchText],
    )

    useEffect(() => {
        if (autocompleteOptions && searchText && searchText.length > 0 && searchText !== initialText) {
            const newOptions = autocompleteOptions.filter(option => filterFunction(searchText, option))
            updateShowAutocompleteOptions(newOptions)
            setFilteredOptions(newOptions)
        } else {
            setShowAutocompleteOptions(false)
            setFilteredOptions([])
        }
    }, [searchText, autocompleteOptions, initialText, updateShowAutocompleteOptions])

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setSearchText(value)
        if (!value) {
            onSubmit('')
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        const key = getKeyboardKey(event)
        switch (key) {
            case 'ArrowDown':
                if (showAutocompleteOptions) {
                    if (selectedAutocompleteOption === null || selectedAutocompleteOption === filteredOptions.length - 1) {
                        setSelectedAutocompleteOption(0)
                    } else {
                        setSelectedAutocompleteOption(selectedAutocompleteOption + 1)
                    }
                }
                break
            case 'ArrowUp':
                if (showAutocompleteOptions) {
                    if (selectedAutocompleteOption === null || selectedAutocompleteOption === 0) {
                        setSelectedAutocompleteOption(filteredOptions.length - 1)
                    } else {
                        setSelectedAutocompleteOption(selectedAutocompleteOption - 1)
                    }
                }
                break
            case 'Enter':
                if (showAutocompleteOptions && selectedAutocompleteOption !== null) {
                    setSearchText(filteredOptions[selectedAutocompleteOption])
                } else {
                    setShowAutocompleteOptions(false)
                    onSubmit(searchText)
                }
                break

            default:
                break
        }
    }

    const handleFocus = () => {
        if (withTypist) {
            setShowTypist(false)
        }
        setIsFocused(true)
    }

    const handleBlur = () => {
        if (withTypist && (!searchText || searchText.length === 0)) {
            setShowTypist(true)
        }
        setIsFocused(false)
    }

    const handleSearchClick = (event: MouseEvent) => {
        onSubmit(searchText)
    }

    const handleClearClick = () => {
        if (onClearClick) {
            onClearClick()
            setSearchText('')
        }
    }

    return (
        <div
            css={css`
                width: 100%;
                display: flex;
                justify-content: center;
                z-index: 2;
                box-shadow: 0 4px 12px ${COLOR_PALETTE.box_shadow_color};
                margin-bottom: 24px;
                ${styles}
            `}
            {...other}
        >
            <div
                css={css`
                    width: 100%;
                    border: 1px solid ${isFocused ? COLOR_PALETTE.gray_3 : COLOR_PALETTE.gray_2};
                    position: relative;
                    height: 50px;
                    background-color: ${COLOR_PALETTE.white};
                    transition: all linear 0.3s;
                `}
            >
                {isOnResultsPage && (
                    <Icon
                        name='close'
                        onClick={handleClearClick}
                        css={css`
                            position: absolute;
                            top: 50%;
                            transform: translateY(-50%);
                            right: 16px;
                            cursor: pointer;
                            z-index: 10;
                        `}
                    />
                )}

                <input
                    css={css`
                        width: 100%;
                        padding: 17px 16px 15px;
                        border: 0;
                        background: transparent;
                        box-sizing: border-box;
                        position: relative;
                        ::placeholder {
                            color: ${COLOR_PALETTE.gray_3};
                        }
                        &:disabled {
                            background-color: ${COLOR_PALETTE.gray_1};
                        }
                    `}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    value={searchText}
                    type='text'
                    placeholder={withTypist ? '' : placeholder}
                    disabled={disabled}
                    data-testid={generateSubTestId(dataTestId, 'input')}
                />
                {showAutocompleteOptions && (
                    <ul
                        css={css`
                            background-color: ${COLOR_PALETTE.white};
                            padding: 8px 16px;
                            margin: 0;
                            border: 1px solid ${isOnResultsPage ? COLOR_PALETTE.gray_2 : COLOR_PALETTE.gray_4};
                        `}
                    >
                        {filteredOptions.map((opt, index) => (
                            <li
                                key={opt}
                                onClick={() => setSearchText(opt)}
                                css={css`
                                    list-style-type: none;
                                    padding: 8px 0;
                                    font-size: 14px;
                                    cursor: pointer;
                                    background-color: ${selectedAutocompleteOption === index ? COLOR_PALETTE.gray_1 : ''};
                                `}
                            >
                                <TextHighlight searchWords={searchText.split(' ')} text={opt} />
                            </li>
                        ))}
                    </ul>
                )}
                {searchText === '' && (
                    <div
                        css={css`
                            color: ${COLOR_PALETTE.gray_3};
                            position: absolute;
                            top: 14px;
                            left: 17px;
                            width: 100%;
                        `}
                    >
                        {showTypist && (
                            <Typist
                                cursor={{ hideWhenDone: true }}
                                css={css`
                                    white-space: nowrap;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    width: calc(100% - 45px);
                                    font-size: 14px;
                                `}
                            >
                                Junior
                                <Typist.Backspace count={6} delay={300} />
                                Senior FrontEnd Developer with Angular
                                <Typist.Backspace count={7} delay={500} />
                                React experienced in Banking
                                <Typist.Backspace count={7} delay={400} />
                                Automotive
                            </Typist>
                        )}
                    </div>
                )}
            </div>
            {disabled ? (
                <Fragment>
                    {showFilterOption && (
                        <ButtonPositioner disabled isFocused={isFocused}>
                            <Icon name='filter' size={16} />
                        </ButtonPositioner>
                    )}

                    <ButtonPositioner disabled isFocused={isFocused}>
                        <Icon name='search' size={16} />
                    </ButtonPositioner>
                </Fragment>
            ) : (
                <Fragment>
                    {showFilterOption && (
                        <ButtonPositioner
                            isFocused={isFocused}
                            onClick={onToggleFilters}
                            data-testid={generateSubTestId(dataTestId, 'filters')}
                        >
                            <IconButton name='filter' pressed={filtersOpened} size={32} innerSize={16} />
                        </ButtonPositioner>
                    )}

                    <ButtonPositioner
                        isFocused={isFocused}
                        onClick={handleSearchClick}
                        data-testid={generateSubTestId(dataTestId, 'apply')}
                    >
                        <Icon name='search' size={16} />
                    </ButtonPositioner>
                </Fragment>
            )}
        </div>
    )
}

type ButtonPositionerProps = {
    isFocused: boolean
    disabled?: boolean
}

const ButtonPositioner = styled.div<ButtonPositionerProps>`
    border-width: 1px;
    border-style: solid;
    border-color: ${(props: any) => (props.isFocused ? COLOR_PALETTE.gray_3 : COLOR_PALETTE.gray_2)};
    width: 48px;
    background-color: ${(props: any) => (props.disabled ? COLOR_PALETTE.gray_1 : COLOR_PALETTE.white)};
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: content-box;
    margin-left: -1px;
    transition: all linear 0.3s;
    cursor: ${(props: any) => (props.disabled ? 'not-allowed' : 'pointer')};
`

export { SearchInput }
